import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import IconTextField from '../../../../shared/ui/IconTextField';
import OtpInput, { emptyOtp, isOtpComplete, otpCode } from '../../../../shared/ui/OtpInput';
import { AUTH_ERROR_CODE, OTP_LENGTH, OTP_RESEND_SECONDS } from '../../constants';
import { isValidEmail } from '../../utils/validators';
import VerificationDialog, { primaryButtonSx, textLinkSx, VerificationTitle } from '../VerificationDialog/VerificationDialog';
import {
  EMAIL_VERIFICATION_COLOR as COLOR,
  EMAIL_VERIFICATION_COPY as COPY,
  EMAIL_VERIFICATION_METRICS as METRICS,
  EMAIL_VERIFICATION_STEP as STEP,
  EmailVerificationStep,
  formatCountdown,
} from './emailVerification.constants';

export type EmailVerificationModalProps = {
  open: boolean;
  onClose?: () => void;
  /** Runs once the email is verified, just before the dialog closes. */
  onVerified?: (email: string) => void;
  /** Filled in on open; a valid address is sent a code straight away. */
  initialEmail?: string;
  /**
   * Send a code to the address. Reject to show an error on the email step.
   * Resolve `{ alreadyVerified: true }` to finish without a code.
   * Omitted in visual tests: the code screen opens straight away.
   */
  onSendCode?: (email: string) => Promise<unknown>;
  /**
   * Check the code. Resolve false (or reject) for a wrong code.
   * Omitted in visual tests: any 6 digits are accepted.
   */
  onVerifyCode?: (email: string, code: string) => Promise<boolean | void>;
};

/** Failures the user can act on keep the server's message; anything else gets the fallback copy. */
const SHOWN_ERROR_CODES: string[] = [
  AUTH_ERROR_CODE.EMAIL_TAKEN,
  AUTH_ERROR_CODE.EMAIL_NOT_ALLOWED,
  AUTH_ERROR_CODE.INVALID_OTP,
  AUTH_ERROR_CODE.OTP_EXPIRED,
  AUTH_ERROR_CODE.TOO_MANY_ATTEMPTS,
];

function errorText(error: unknown, fallback: string) {
  const coded = error as { code?: string; message?: string } | null;
  if (coded && coded.code && coded.message && SHOWN_ERROR_CODES.includes(coded.code)) return coded.message;
  return fallback;
}

function isAlreadyVerified(result: unknown) {
  return Boolean(result && (result as { alreadyVerified?: boolean }).alreadyVerified);
}

type EmailVerificationModalState = {
  step: EmailVerificationStep;
  email: string;
  emailTouched: boolean;
  digits: string[];
  busy: boolean;
  errorMessage: string;
  secondsLeft: number;
  /** Bumped on every new code so the boxes take focus again. */
  sendKey: number;
};

const INITIAL_STATE: EmailVerificationModalState = {
  step: STEP.EMAIL,
  email: '',
  emailTouched: false,
  digits: emptyOtp(OTP_LENGTH),
  busy: false,
  errorMessage: '',
  secondsLeft: 0,
  sendKey: 0,
};

/**
 * Email → 6-digit code → closes once verified. Pass onSendCode and onVerifyCode
 * to wire it to the API. The code submits on its own once all six digits are in.
 */
class EmailVerificationModal extends React.Component<EmailVerificationModalProps, EmailVerificationModalState> {
  state: EmailVerificationModalState = { ...INITIAL_STATE, digits: emptyOtp(OTP_LENGTH) };

  private timer: ReturnType<typeof setInterval> | null = null;

  componentDidMount() {
    if (this.props.open) this.startWithInitialEmail();
  }

  componentDidUpdate(prevProps: EmailVerificationModalProps) {
    if (this.props.open && !prevProps.open) this.startWithInitialEmail();
  }

  componentWillUnmount() {
    this.stopTimer();
  }

  startWithInitialEmail() {
    const email = (this.props.initialEmail || '').trim();
    if (!email) return;
    this.setState({ email }, () => {
      if (isValidEmail(email)) this.handleSend();
    });
  }

  /** Verified (or already was): close straight away, no success screen. */
  finish(email: string) {
    this.stopTimer();
    this.setState({ busy: false });
    if (this.props.onVerified) this.props.onVerified(email);
    if (this.props.onClose) this.props.onClose();
  }

  startTimer() {
    this.stopTimer();
    this.setState({ secondsLeft: OTP_RESEND_SECONDS });
    this.timer = setInterval(() => {
      this.setState((current) => {
        if (current.secondsLeft <= 1) this.stopTimer();
        return { secondsLeft: Math.max(current.secondsLeft - 1, 0) };
      });
    }, 1000);
  }

  stopTimer() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  /** After the close animation, so the dialog does not flash back to the first step. */
  handleExited = () => {
    this.stopTimer();
    this.setState({ ...INITIAL_STATE, digits: emptyOtp(OTP_LENGTH) });
  };

  handleClose = () => {
    if (this.props.onClose) this.props.onClose();
  };

  handleEmailChange = (email: string) => {
    this.setState({ email, errorMessage: '' });
  };

  sendCode = async () => {
    const email = this.state.email.trim();
    return this.props.onSendCode ? this.props.onSendCode(email) : undefined;
  };

  handleSend = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    this.setState({ emailTouched: true });
    if (!isValidEmail(this.state.email) || this.state.busy) return;
    this.setState({ busy: true, errorMessage: '' });
    try {
      if (isAlreadyVerified(await this.sendCode())) {
        this.finish(this.state.email.trim());
        return;
      }
      this.setState((current) => ({
        step: STEP.OTP,
        email: current.email.trim(),
        busy: false,
        digits: emptyOtp(OTP_LENGTH),
        sendKey: current.sendKey + 1,
      }));
      this.startTimer();
    } catch (error) {
      this.setState({ busy: false, errorMessage: errorText(error, COPY.sendFailed) });
    }
  };

  handleResend = async () => {
    if (this.state.busy || this.state.secondsLeft > 0) return;
    this.setState((current) => ({
      digits: emptyOtp(OTP_LENGTH),
      errorMessage: '',
      sendKey: current.sendKey + 1,
    }));
    this.startTimer();
    try {
      await this.sendCode();
    } catch (error) {
      this.setState({ errorMessage: errorText(error, COPY.sendFailed) });
    }
  };

  handleChangeEmail = () => {
    this.stopTimer();
    this.setState({ step: STEP.EMAIL, errorMessage: '', secondsLeft: 0 });
  };

  handleDigits = (digits: string[]) => {
    this.setState({ digits, errorMessage: '' }, () => {
      if (isOtpComplete(digits)) this.handleVerify();
    });
  };

  handleVerify = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    if (!isOtpComplete(this.state.digits) || this.state.busy) return;
    const email = this.state.email.trim();
    this.setState({ busy: true, errorMessage: '' });
    let failure = '';
    try {
      if (this.props.onVerifyCode && (await this.props.onVerifyCode(email, otpCode(this.state.digits))) === false) {
        failure = COPY.wrongCode;
      }
    } catch (error) {
      failure = errorText(error, COPY.wrongCode);
    }
    if (failure) {
      this.setState({ busy: false, errorMessage: failure });
      return;
    }
    this.finish(email);
  };

  renderEmailStep() {
    const emailInvalid = this.state.emailTouched && Boolean(this.state.email) && !isValidEmail(this.state.email);
    return (
      <Box component="form" onSubmit={this.handleSend} noValidate>
        <VerificationTitle title={COPY.emailTitle} subtitle={COPY.emailSubtitle} />
        <IconTextField
          id="email-verification-email"
          label={COPY.emailLabel}
          type="email"
          inputMode="email"
          autoComplete="email"
          autoFocus
          placeholder={COPY.emailPlaceholder}
          icon={<MailOutlineRoundedIcon sx={{ fontSize: 20 }} />}
          value={this.state.email}
          onChange={this.handleEmailChange}
          onBlur={() => this.setState({ emailTouched: true })}
          error={emailInvalid}
          helperText={emailInvalid ? COPY.emailInvalid : undefined}
          // No empty line under the field: the message takes space only when there is one.
          reserveHelperSpace={false}
        />
        {this.state.errorMessage ? (
          <Box component="p" role="alert" sx={{ m: 0, mt: 1, fontSize: 13, color: COLOR.error }}>
            {this.state.errorMessage}
          </Box>
        ) : null}
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!this.state.email || this.state.busy}
          sx={{ ...primaryButtonSx, mt: 2 }}
        >
          {this.state.busy ? <CircularProgress size={22} sx={{ color: COLOR.onBrand }} /> : COPY.sendCode}
        </Button>
      </Box>
    );
  }

  renderOtpStep() {
    const { errorMessage, secondsLeft, busy } = this.state;
    return (
      <Box component="form" onSubmit={this.handleVerify} noValidate>
        <VerificationTitle title={COPY.otpTitle} subtitle={COPY.otpSubtitle} />
        <IconTextField
          id="email-verification-sent-to"
          label={COPY.emailLabel}
          value={this.state.email}
          readOnly
          reserveHelperSpace={false}
          icon={<MailOutlineRoundedIcon sx={{ fontSize: 20 }} />}
          endAction={(
            <Link component="button" type="button" underline="hover" onClick={this.handleChangeEmail} sx={textLinkSx}>
              {COPY.change}
            </Link>
          )}
          sx={{ mb: 2.5 }}
        />

        <Box component="p" id="email-verification-otp-label" sx={{ m: 0, mb: 1, fontSize: 13, fontWeight: 600, color: COLOR.text }}>
          {COPY.otpLabel}
        </Box>
        <OtpInput
          value={this.state.digits}
          onChange={this.handleDigits}
          labelledBy="email-verification-otp-label"
          focusKey={this.state.sendKey}
          error={Boolean(errorMessage)}
          disabled={busy}
          boxHeight={METRICS.otpBoxHeight}
          gap={METRICS.otpGap}
          radius={METRICS.otpRadius}
          digitSx={{ fontSize: 20, fontWeight: 600 }}
          colors={{ field: COLOR.field, active: COLOR.brand, text: COLOR.text, error: COLOR.error }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.25, mb: 2.5, minHeight: 20 }}>
          {errorMessage ? (
            <Box component="p" role="alert" sx={{ m: 0, fontSize: 12.5, color: COLOR.error }}>
              {errorMessage}
            </Box>
          ) : secondsLeft > 0 ? (
            <Box component="p" sx={{ m: 0, fontSize: 12.5, color: COLOR.muted }}>
              {COPY.resendIn(formatCountdown(secondsLeft))}
            </Box>
          ) : (
            <span />
          )}
          {secondsLeft === 0 ? (
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={this.handleResend}
              disabled={busy}
              sx={{ ...textLinkSx, fontSize: 12.5 }}
            >
              {COPY.resend}
            </Link>
          ) : null}
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={!isOtpComplete(this.state.digits) || busy}
          sx={primaryButtonSx}
        >
          {busy ? <CircularProgress size={22} sx={{ color: COLOR.onBrand }} /> : COPY.verify}
        </Button>
      </Box>
    );
  }

  render() {
    const { step, busy } = this.state;
    return (
      <VerificationDialog
        open={this.props.open}
        busy={busy}
        onClose={this.handleClose}
        onExited={this.handleExited}
        stepKey={step}
      >
        {step === STEP.EMAIL ? this.renderEmailStep() : null}
        {step === STEP.OTP ? this.renderOtpStep() : null}
      </VerificationDialog>
    );
  }
}

export default EmailVerificationModal;
